import React from "react"
import LoginForm from "./LoginForm"
import { UserProfile } from "../types/objects"
import { ADMIN_ROLE } from "../types/roles"
import { INTERNAL_TABS } from "./internal/tabs"
import { ShieldCheck, User as UserIcon, BarChart3, X, ArrowRight, LogOut } from "lucide-react"

interface MobileMenuProps {
    sections: any[]
    activeSection: string | null
    onSelect: (sectionId: string | null) => void
    isOpen: boolean
    user: UserProfile | null
    onLogin: (user: UserProfile, token: string) => void
    onLogout: () => void
}

/**
 * MobileMenu Component
 * Slide-in panel with the public anchors (or CRM modules when logged in),
 * a primary CTA and the login form.
 */
export const MobileMenu: React.FC<MobileMenuProps> = ({
    sections,
    activeSection,
    onSelect,
    isOpen,
    user,
    onLogin,
    onLogout
}) => {
    const isAdmin = user?.role?.name === ADMIN_ROLE

    // Map section IDs to human-readable Czech labels
    const labelMap: Record<string, string> = {
        features: "Charakteristika",
        about: "O nás",
        services: "Služby",
        products: "Produkty",
        inquiry: "Poptávka",
        pricing: "Ceník",
        portfolio: "Galerie",
        testimonials: "Reference",
        team: "Tým",
        contact: "Kontakt",
        faq: "Časté dotazy",
        messages: "Zprávy",
        documents: "Dokumenty",
        contracts: "Smlouvy",
        contacts: "Kontakty",
        scoring: "Screening",
        reports: "Reporty"
    }

    // Internal navigation items (shared definition with the dashboard sidebar)
    const internalItems = INTERNAL_TABS.map(tab => ({ id: tab.key, label: tab.label }))

    /**
     * Unified handler for navigation and overlay actions.
     * Uses preventDefault and stopPropagation to isolate the event from browser interference.
     */
    const handleAction = (e: React.MouseEvent | React.PointerEvent, id: string | null) => {
        e.preventDefault()
        e.stopPropagation()
        onSelect(id)

        if (id) {
            const el = document.getElementById(id)
            if (el) {
                // Short delay ensures the menu starts closing before the scroll begins
                setTimeout(() => {
                    el.scrollIntoView({ behavior: "smooth" })
                }, 100)
            }
        }
    }

    // Filter items based on whether the user is logged in
    const displayItems = user
        ? internalItems
        : sections.filter(s => (typeof s === "string" ? s !== "login" : s.id !== "login"))

    return (
        <>
            <div
                className={`lp-mobile__overlay${isOpen ? " is-open" : ""}`}
                onPointerDown={(e) => handleAction(e, null)}
            />

            <nav
                className={`lp-mobile${isOpen ? " is-open" : ""}`}
                onPointerDown={(e) => e.stopPropagation()}
                aria-hidden={!isOpen}
                aria-label="Mobilní navigace"
            >
                <div className="lp-mobile__head">
                    <span className="lp-mobile__brand">
                        <img src="/img/b2bticon.png" alt="" aria-hidden="true" />
                        B2B Insight
                    </span>
                    <button
                        type="button"
                        className="lp-mobile__close"
                        aria-label="Zavřít menu"
                        onPointerDown={(e) => handleAction(e, null)}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="lp-mobile__body">
                    <span className="lp-mobile__label">{user ? "CRM moduly" : "Navigace"}</span>

                    <ul className="lp-mobile__list">
                        {displayItems.map((item, index) => {
                            const id = typeof item === "string" ? item : item.id
                            const label = typeof item === "string" ? (labelMap[item] || item) : item.label
                            const isActive = activeSection === id
                            const isScoring = id === "scoring"

                            return (
                                <li
                                    key={id}
                                    style={{
                                        opacity: isOpen ? 1 : 0,
                                        transform: isOpen ? "none" : "translateX(16px)",
                                        transition: `opacity 0.35s ease ${index * 0.03}s, transform 0.35s ease ${index * 0.03}s`
                                    }}
                                >
                                    <a
                                        href={`#${id}`}
                                        onPointerDown={(e) => handleAction(e, id)}
                                        className={`lp-mobile__link${isActive ? " is-active" : ""}${isScoring ? " lp-mobile__link--accent" : ""}`}
                                    >
                                        {isScoring && <BarChart3 size={18} />}
                                        {label}
                                        <ArrowRight size={16} className="lp-mobile__arrow" />
                                    </a>
                                </li>
                            )
                        })}
                    </ul>

                    {!user && (
                        <a
                            href="#contact"
                            className="lp-btn lp-btn--primary lp-btn--block"
                            onPointerDown={(e) => handleAction(e, "contact")}
                        >
                            Konzultace zdarma <ArrowRight size={18} />
                        </a>
                    )}

                    <div className="lp-mobile__account">
                        {user ? (
                            <div className={`lp-mobile__user${isAdmin ? " lp-mobile__user--admin" : ""}`}>
                                <div className="lp-mobile__user-role">
                                    {isAdmin ? <ShieldCheck size={16} /> : <UserIcon size={16} />}
                                    {isAdmin ? "Administrátor" : "Uživatel"}
                                </div>
                                <p className="lp-mobile__user-name">{user.username}</p>
                                <button
                                    type="button"
                                    className="lp-mobile__logout"
                                    onPointerDown={(e) => { e.stopPropagation(); onLogout() }}
                                >
                                    <LogOut size={18} /> Odhlásit se
                                </button>
                            </div>
                        ) : (
                            <div className="lp-mobile__login">
                                <span className="lp-mobile__label">Přihlášení do CRM</span>
                                <LoginForm onLogin={onLogin} />
                            </div>
                        )}
                    </div>
                </div>
            </nav>
        </>
    )
}
